import { useRef, useEffect, useState } from 'react';
import { Send, Loader2, Sparkles, TrendingUp, HelpCircle, BarChart3, AlertTriangle, Plus, Trash2, MessageSquare, PanelLeftClose, PanelLeft, X } from 'lucide-react';
import { clsx } from 'clsx';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useChat } from '../hooks/useChat';
import { useAuthStore } from '../store';
import { LockedFeature } from '../components/common/LockedFeature';
import { processTickerLinks } from '../components/chat/TickerLink';
import { ToolActivityIndicator } from '../components/chat/ToolActivityIndicator';
import { ChatChart } from '../components/chat/ChatChart';

// Tier level mapping
const tierLevel: Record<string, number> = {
  'free': 0,
  'starter': 1,
  'early_supporter': 2,
  'pro': 2,
  'enterprise': 3
};

interface SuggestedPrompt {
  title: string;
  prompt: string;
  icon: string;
}

const SUGGESTED_PROMPTS: SuggestedPrompt[] = [
  {
    title: "Market Overview",
    prompt: "What's the current market sentiment across the top stocks?",
    icon: "chart",
  },
  {
    title: "Buy Opportunities",
    prompt: "Which stocks have the highest disconnect scores right now?",
    icon: "trending-up",
  },
  {
    title: "Explain Disconnect",
    prompt: "How does the disconnect score work and what does it mean?",
    icon: "help",
  },
  {
    title: "Risk Analysis",
    prompt: "What stocks are showing sell signals based on sentiment?",
    icon: "alert",
  }
];

const getIcon = (iconName: string) => {
  switch (iconName) {
    case 'chart': return BarChart3;
    case 'trending-up': return TrendingUp;
    case 'help': return HelpCircle;
    case 'alert': return AlertTriangle;
    default: return Sparkles;
  }
};

export function Chat() {
  const { isAuthenticated, user } = useAuthStore();
  const userTier = user?.subscription_tier || 'free';
  const hasAccess = tierLevel[userTier] >= 1; // Starter or higher

  const {
    conversations,
    activeConversationId,
    messages,
    isLoading,
    sendMessage,
    startNewChat,
    loadConversations,
    loadConversation,
    deleteConversation,
  } = useChat();

  // Default sidebar collapsed
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isNearBottomRef = useRef(true);

  // Load conversations on mount
  useEffect(() => {
    if (isAuthenticated) {
      loadConversations();
    }
  }, [isAuthenticated, loadConversations]);

  // Track whether user is scrolled near the bottom
  const handleMessagesScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    isNearBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < 100;
  };

  // Auto-scroll during streaming: instant scrollTop (no smooth animation overlap)
  useEffect(() => {
    if (!isNearBottomRef.current) return;
    const el = messagesContainerRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const input = inputRef.current;
    if (input && input.value.trim()) {
      sendMessage(input.value);
      input.value = '';
    }
  };

  const chatContent = (
    <div className="h-full flex relative">
      {/* Toggle button when sidebar is collapsed - hidden on mobile */}
      {isAuthenticated && !sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(true)}
          className={clsx(
            'absolute left-0 top-0 z-20 p-2.5 rounded-xl transition-all cursor-pointer',
            'bg-[var(--bg-card)] border border-[var(--border-color)]',
            'text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-elevated)]',
            'hidden md:flex' // Hide on mobile
          )}
          title="Show chat history"
        >
          <PanelLeft className="h-5 w-5" />
        </button>
      )}

      {/* Sidebar - Conversations History */}
      {/* On mobile: full screen overlay. On desktop: slide in/out */}
      {isAuthenticated && sidebarOpen && (
        <>
          {/* Mobile backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-30 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
          <div
            className={clsx(
              'flex-shrink-0 flex flex-col bg-[var(--bg-card)] overflow-hidden transition-all duration-300 ease-in-out z-40',
              // Mobile: fixed full height overlay
              'fixed inset-y-0 left-0 w-72 md:relative md:inset-auto',
              // Desktop: slide in with border and rounded corners
              'md:rounded-xl md:border md:border-[var(--border-color)] md:w-64 md:mr-6'
            )}
          >
            {/* Sidebar content */}
            <div className="flex flex-col h-full">
              {/* Header with toggle and New Chat button */}
              <div className="p-3 border-b border-[var(--border-color)] flex items-center gap-2">
                <button
                  onClick={() => setSidebarOpen(false)}
                  className={clsx(
                    'p-2 rounded-lg transition-all cursor-pointer',
                    'text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-elevated)]'
                  )}
                  title="Hide chat history"
                >
                  <X className="h-5 w-5 md:hidden" />
                  <PanelLeftClose className="h-4 w-4 hidden md:block" />
                </button>
                <button
                  onClick={() => {
                    startNewChat();
                    setSidebarOpen(false);
                  }}
                  className={clsx(
                    'flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition-all cursor-pointer',
                    'bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)]',
                    'text-[var(--bg-primary)] font-medium text-sm'
                  )}
                >
                  <Plus className="h-4 w-4" />
                  New Chat
                </button>
              </div>

              {/* Conversations List */}
              <div className="flex-1 overflow-y-auto p-2">
                {conversations.length === 0 ? (
                  <p className="text-center text-sm text-[var(--text-muted)] py-8">
                    No conversations yet
                  </p>
                ) : (
                  <div className="space-y-1">
                    {conversations.map((conv) => (
                      <div
                        key={conv.id}
                        className={clsx(
                          'group flex items-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer transition-all',
                          activeConversationId === conv.id
                            ? 'bg-[var(--color-accent)]/10 text-[var(--text-primary)]'
                            : 'hover:bg-[var(--bg-secondary)] text-[var(--text-secondary)]'
                        )}
                        onClick={() => {
                          loadConversation(conv.id);
                          setSidebarOpen(false);
                        }}
                      >
                        <MessageSquare className="h-4 w-4 flex-shrink-0" />
                        <span className="flex-1 text-sm truncate">{conv.title}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteConversation(conv.id);
                          }}
                          className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-[var(--bg-elevated)] text-[var(--text-muted)] hover:text-red-500 transition-all"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Main chat area - Centers within remaining space */}
      <div className="flex-1 flex flex-col items-center min-w-0">
        <div className="w-full max-w-3xl h-full flex flex-col">
          {/* Mobile header with history toggle */}
          {isAuthenticated && (
            <div className="flex items-center justify-between px-4 py-2 border-b border-[var(--border-color)] md:hidden">
              <button
                onClick={() => setSidebarOpen(true)}
                className={clsx(
                  'flex items-center gap-2 px-3 py-2 rounded-lg transition-all',
                  'bg-[var(--bg-card)] border border-[var(--border-color)]',
                  'text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                )}
              >
                <MessageSquare className="h-4 w-4" />
                <span className="text-sm">History</span>
              </button>
              <button
                onClick={startNewChat}
                className={clsx(
                  'flex items-center gap-2 px-3 py-2 rounded-lg transition-all',
                  'bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)]',
                  'text-[var(--bg-primary)] text-sm font-medium'
                )}
              >
                <Plus className="h-4 w-4" />
                <span>New</span>
              </button>
            </div>
          )}

          {/* Messages area */}
          <div
            ref={messagesContainerRef}
            onScroll={handleMessagesScroll}
            className={clsx(
              "flex-1 px-4 py-4 sm:py-6 scrollbar-hide",
              messages.length > 0 ? "overflow-y-auto" : "overflow-hidden"
            )}
          >
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center px-2">
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-[var(--color-accent)]/10 flex items-center justify-center mb-4 md:mb-6">
                  <Sparkles className="h-7 w-7 md:h-8 md:w-8 text-[var(--color-accent)]" />
                </div>
                <h1 className="text-xl md:text-2xl font-bold text-[var(--text-primary)] mb-2">
                  SentimentEdge AI
                </h1>
                <p className="text-sm md:text-base text-[var(--text-muted)] max-w-md mb-6 md:mb-10">
                  Ask about market sentiment, find opportunities, or analyze stocks.
                </p>

                {/* Single column on mobile, 2 columns on tablet+ */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 w-full max-w-xl">
                  {SUGGESTED_PROMPTS.map((prompt, i) => {
                    const Icon = getIcon(prompt.icon);
                    return (
                      <button
                        key={i}
                        onClick={() => sendMessage(prompt.prompt)}
                        className={clsx(
                          'flex items-center gap-3 p-3 sm:p-4 rounded-xl transition-all text-left cursor-pointer',
                          'bg-[var(--bg-card)] hover:bg-[var(--bg-elevated)] active:scale-[0.98]',
                          'border border-[var(--border-color)] hover:border-[var(--color-accent)]/30',
                          'group'
                        )}
                      >
                        <div className="w-9 h-9 rounded-lg bg-[var(--color-accent)]/10 flex items-center justify-center flex-shrink-0">
                          <Icon className="h-4 w-4 text-[var(--color-accent)]" />
                        </div>
                        <span className="text-sm text-[var(--text-secondary)] group-hover:text-[var(--text-primary)]">
                          {prompt.title}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="space-y-4 sm:space-y-6">
                {messages.map((message) => (
                  <div key={message.id}>
                    {message.role === 'user' ? (
                      <div className="flex justify-end">
                        <div className="max-w-[85%] sm:max-w-[70%] rounded-2xl rounded-br-md px-4 py-2.5 sm:px-5 sm:py-3 bg-[var(--color-accent)] text-[var(--bg-primary)]">
                          <p className="text-sm sm:text-base">{message.content}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="text-[var(--text-primary)] text-sm sm:text-base">
                        {message.toolActivity && message.toolActivity.length > 0 && (
                          <ToolActivityIndicator activities={message.toolActivity} />
                        )}
                        {message.chartData && message.chartData.length > 0 && (
                          message.chartData.map((chart, i) => (
                            <ChatChart key={`chart-${i}`} chart={chart} />
                          ))
                        )}
                        {message.content ? (
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                              h1: ({ children }) => <h1 className="text-lg sm:text-xl font-bold mt-4 sm:mt-6 mb-2 sm:mb-3">{children}</h1>,
                              h2: ({ children }) => <h2 className="text-base sm:text-lg font-bold mt-4 sm:mt-5 mb-2">{children}</h2>,
                              h3: ({ children }) => <h3 className="text-sm sm:text-base font-bold mt-3 sm:mt-4 mb-1.5 sm:mb-2">{children}</h3>,
                              h4: ({ children }) => <h4 className="text-sm font-semibold mt-3 sm:mt-4 mb-1">{children}</h4>,
                              p: ({ children }) => {
                                // Process children to linkify tickers
                                const processedChildren = Array.isArray(children)
                                  ? children.flatMap((child) =>
                                      typeof child === 'string' ? processTickerLinks(child) : child
                                    )
                                  : typeof children === 'string'
                                  ? processTickerLinks(children)
                                  : children;
                                return <p className="leading-relaxed mb-3 sm:mb-4">{processedChildren}</p>;
                              },
                              ul: ({ children }) => <ul className="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4 ml-4 sm:ml-5 list-disc">{children}</ul>,
                              ol: ({ children }) => <ol className="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4 ml-4 sm:ml-5 list-decimal">{children}</ol>,
                              li: ({ children }) => {
                                // Process children to linkify tickers in list items
                                const processedChildren = Array.isArray(children)
                                  ? children.flatMap((child) =>
                                      typeof child === 'string' ? processTickerLinks(child) : child
                                    )
                                  : typeof children === 'string'
                                  ? processTickerLinks(children)
                                  : children;
                                return <li>{processedChildren}</li>;
                              },
                              strong: ({ children }) => {
                                // Process strong text for tickers
                                const processedChildren = typeof children === 'string'
                                  ? processTickerLinks(children)
                                  : children;
                                return <strong className="font-semibold text-[var(--color-accent)]">{processedChildren}</strong>;
                              },
                              code: ({ children, className }) => {
                                const isInline = !className;
                                return isInline ? (
                                  <code className="px-1.5 py-0.5 rounded bg-[var(--bg-card)] text-[var(--color-accent)] text-sm font-mono">{children}</code>
                                ) : (
                                  <code className={className}>{children}</code>
                                );
                              },
                              pre: ({ children }) => <pre className="p-3 sm:p-4 rounded-xl bg-[var(--bg-card)] border border-[var(--border-color)] overflow-x-auto mb-3 sm:mb-4 text-xs sm:text-sm">{children}</pre>,
                              table: ({ children }) => (
                                <div className="overflow-x-auto mb-3 sm:mb-4 rounded-xl border border-[var(--border-color)]">
                                  <table className="w-full text-xs sm:text-sm">{children}</table>
                                </div>
                              ),
                              thead: ({ children }) => <thead className="bg-[var(--bg-card)]">{children}</thead>,
                              tbody: ({ children }) => <tbody className="divide-y divide-[var(--border-color)]">{children}</tbody>,
                              tr: ({ children }) => <tr className="hover:bg-[var(--bg-secondary)]/50">{children}</tr>,
                              th: ({ children }) => <th className="px-2 sm:px-4 py-2 sm:py-3 text-left font-semibold text-[var(--text-primary)] border-b border-[var(--border-color)] whitespace-nowrap">{children}</th>,
                              td: ({ children }) => {
                                // Process table cells for tickers
                                const processedChildren = typeof children === 'string'
                                  ? processTickerLinks(children)
                                  : children;
                                return <td className="px-2 sm:px-4 py-2 sm:py-3 text-[var(--text-secondary)]">{processedChildren}</td>;
                              },
                            }}
                          >
                            {message.content}
                          </ReactMarkdown>
                        ) : (
                          <div className="flex items-center gap-2 text-[var(--text-muted)]">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>Thinking...</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}

              </div>
            )}
          </div>

          {/* Input area - floating at bottom */}
          <div className="px-3 sm:px-4 py-3 sm:py-4">
            <form onSubmit={handleSubmit} className="flex gap-2 sm:gap-3">
              <input
                ref={inputRef}
                type="text"
                placeholder="Ask about stocks..."
                disabled={isLoading}
                className={clsx(
                  'flex-1 px-4 py-3 sm:px-5 sm:py-3.5 rounded-xl text-base',
                  'bg-[var(--bg-card)] text-[var(--text-primary)]',
                  'border border-[var(--border-color)]',
                  'placeholder:text-[var(--text-muted)]',
                  'focus:outline-none focus:border-[var(--color-accent)]/50',
                  'disabled:opacity-50 transition-all'
                )}
              />
              <button
                type="submit"
                disabled={isLoading}
                className={clsx(
                  'px-4 py-3 sm:px-5 sm:py-3.5 rounded-xl transition-all flex items-center justify-center',
                  'bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] active:scale-95',
                  'text-[var(--bg-primary)] font-medium',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'min-w-[48px]'
                )}
              >
                {isLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Send className="h-5 w-5" />
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );

  // For free users, show locked preview
  if (!hasAccess) {
    return (
      <LockedFeature feature="AI Chat assistant to analyze stocks and market sentiment" requiredTier="starter">
        {chatContent}
      </LockedFeature>
    );
  }

  return chatContent;
}
